import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtMoney } from "@/lib/api";
import { useMasters, opt } from "@/lib/useMasters";
import { PageHeader, Card, Field, SelectInput, TextInput, NumberInput, TextArea, StatusBadge, CrudActions } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Calculator, HandCoins } from "lucide-react";

export default function Payments() {
  return (
    <div data-testid="payments-page">
      <PageHeader title="Payments & Settlement" subtitle="Record party payments and settle labour contractors to clear balances" />
      <Tabs defaultValue="payments">
        <TabsList className="mb-4">
          <TabsTrigger value="payments" data-testid="tab-payments">Party Payments</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="tab-settlements">Labour Settlement</TabsTrigger>
        </TabsList>
        <TabsContent value="payments"><PartyPayments /></TabsContent>
        <TabsContent value="settlements"><Settlements /></TabsContent>
      </Tabs>
    </div>
  );
}

function PartyPayments() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["payments"], queryFn: async () => (await api.get("/finance/payments")).data });

  const refreshPayments = () => {
    qc.invalidateQueries({ queryKey: ["payments"] });
    qc.invalidateQueries({ queryKey: ["cash-balance"] });
    qc.invalidateQueries({ queryKey: ["cash-statement"] });
    qc.invalidateQueries({ queryKey: ["party-view"] });
  };

  const remove = async (p) => {
    if (!window.confirm(`Reverse and delete payment ${p.payment_number}?`)) return;
    try {
      await api.delete(`/finance/payments/${p.id}`);
      toast.success("Payment deleted — party ledger & manager cash updated");
      refreshPayments();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button data-testid="add-payment-btn" onClick={() => { setEditing(null); setOpen(true); }} className="flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900">
          <Plus size={16} /> Record Payment
        </button>
      </div>
      {isLoading ? <Spin /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
                <tr><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Party</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Reference</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {data.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">No payments</td></tr>}
                {data.map((p) => (
                  <tr key={p.id} className="border-t border-stone-100 even:bg-stone-50/50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-700">{p.payment_number}</td>
                    <td className="px-4 py-3">{p.date}</td>
                    <td className="px-4 py-3">{p.party_name}</td>
                    <td className="px-4 py-3">{p.mode}</td>
                    <td className="px-4 py-3 text-stone-500">{p.reference || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-700">{fmtMoney(p.amount)}</td>
                    <td className="px-4 py-3"><CrudActions testid={`payment-${p.id}`} onEdit={() => { setEditing(p); setOpen(true); }} onDelete={() => remove(p)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <PaymentDialog open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={() => qc.invalidateQueries()} />
    </div>
  );
}

function PaymentDialog({ open, onClose, onSaved, record }) {
  const m = useMasters();
  const blank = { party_id: "", amount: "", mode: "Cash", reference: "", date: new Date().toISOString().slice(0, 10), remarks: "" };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (record) setF({ party_id: record.party_id || "", amount: record.amount ?? "", mode: record.mode || "Cash", reference: record.reference || "", date: (record.date || "").slice(0, 10), remarks: record.remarks || "" });
    else setF(blank);
  }, [open, record]);

  const save = async () => {
    if (!f.party_id || !f.amount) return toast.error("Party and amount are required");
    setSaving(true);
    try {
      const body = { ...f, party_id: +f.party_id, amount: +f.amount };
      if (record) {
        const { data } = await api.put(`/finance/payments/${record.id}`, body);
        toast.success(`${data.payment_number} updated (${fmtMoney(data.amount)})`);
      } else {
        const { data } = await api.post("/finance/payments", body);
        toast.success(`${data.payment_number} recorded (${fmtMoney(data.amount)})`);
      }
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="font-display">{record ? "Edit Payment" : "Record Payment"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Party" required className="col-span-2"><SelectInput options={opt(m.parties, (p) => `${p.name} (${p.code})`)} value={f.party_id} onChange={(e) => setF({ ...f, party_id: e.target.value })} data-testid="payment-party" /></Field>
          <Field label="Amount (₹)" required><NumberInput value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payment-amount" /></Field>
          <Field label="Date"><TextInput type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
          <Field label="Mode"><SelectInput options={["Cash", "Bank", "UPI", "Cheque"].map((x) => ({ value: x, label: x }))} value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} /></Field>
          <Field label="Reference"><TextInput value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></Field>
          <Field label="Remarks" className="col-span-2"><TextArea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} /></Field>
        </div>
        <button onClick={save} disabled={saving} data-testid="save-payment-btn" className="mt-2 flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <HandCoins size={18} />} {record ? "Update Payment" : "Save Payment"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function Settlements() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["settlements"], queryFn: async () => (await api.get("/finance/settlements")).data });

  const refreshSettlements = () => {
    qc.invalidateQueries({ queryKey: ["settlements"] });
    qc.invalidateQueries({ queryKey: ["cash-balance"] });
    qc.invalidateQueries({ queryKey: ["cash-statement"] });
    qc.invalidateQueries({ queryKey: ["party-view"] });
  };

  const remove = async (s) => {
    if (!window.confirm(`Reverse ledger/cash and delete settlement ${s.settlement_number}?`)) return;
    try {
      await api.delete(`/finance/settlements/${s.id}`);
      toast.success("Settlement deleted — contractor ledger & manager cash updated");
      refreshSettlements();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <button data-testid="new-settlement-btn" onClick={() => { setEditing(null); setOpen(true); }} className="flex h-10 items-center gap-2 rounded-full bg-emerald-800 px-4 text-sm font-semibold text-white hover:bg-emerald-900">
          <Calculator size={16} /> New Settlement
        </button>
      </div>
      {isLoading ? <Spin /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">
                <tr><th className="px-4 py-3">Settlement</th><th className="px-4 py-3">Contractor</th><th className="px-4 py-3">Period</th><th className="px-4 py-3 text-right">Work</th><th className="px-4 py-3 text-right">Dala</th><th className="px-4 py-3 text-right">Payable</th><th className="px-4 py-3 text-right">Paid</th><th className="px-4 py-3 text-right">Balance</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {data.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-stone-400">No settlements</td></tr>}
                {data.map((s) => (
                  <tr key={s.id} className="border-t border-stone-100 even:bg-stone-50/50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-emerald-700">{s.settlement_number}</td>
                    <td className="px-4 py-3">{s.contractor_name}</td>
                    <td className="px-4 py-3 text-xs">{s.period_from} → {s.period_to}</td>
                    <td className="px-4 py-3 text-right">{fmtMoney(s.work_payment_total)}</td>
                    <td className="px-4 py-3 text-right">{fmtMoney(s.dala_total)}</td>
                    <td className="px-4 py-3 text-right font-semibold">{fmtMoney(s.total_payable)}</td>
                    <td className="px-4 py-3 text-right text-emerald-700">{fmtMoney(s.payments_made)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-amber-700">{fmtMoney(s.balance_payable)}</td>
                    <td className="px-4 py-3"><CrudActions testid={`settlement-${s.id}`} onEdit={() => { setEditing(s); setOpen(true); }} onDelete={() => remove(s)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <SettlementDialog open={open} record={editing} onClose={() => { setOpen(false); setEditing(null); }} onSaved={refreshSettlements} />
    </div>
  );
}

function SettlementDialog({ open, onClose, onSaved, record }) {
  const m = useMasters();
  const blank = { contractor_id: "", period_from: "", period_to: "", previous_advance: "", payments_made: "", other_earnings: "", remarks: "" };
  const [f, setF] = useState(blank);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const contractors = (m.parties || []).filter((p) => p.active !== false && (p.roles || []).includes("Labour Contractor"));

  useEffect(() => {
    if (!open) return;
    if (record) {
      setF({
        contractor_id: record.contractor_id || "", period_from: (record.period_from || "").slice(0, 10),
        period_to: (record.period_to || "").slice(0, 10), previous_advance: record.previous_advance ?? "",
        payments_made: record.payments_made ?? "", other_earnings: record.other_earnings ?? "", remarks: record.remarks || "",
      });
      setPreview({
        entries: "—", work_payment_total: record.work_payment_total, dala_total: record.dala_total,
        total_payable: (Number(record.work_payment_total) || 0) + (Number(record.dala_total) || 0),
      });
    } else { setF(blank); setPreview(null); }
  }, [open, record]);

  const calc = async () => {
    if (!f.contractor_id || !f.period_from || !f.period_to) return toast.error("Select contractor and period");
    setBusy(true);
    try { const { data } = await api.post("/finance/settlements/calculate", { contractor_id: +f.contractor_id, period_from: f.period_from, period_to: f.period_to }); setPreview(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { contractor_id: +f.contractor_id, period_from: f.period_from, period_to: f.period_to, previous_advance: +f.previous_advance || 0, payments_made: +f.payments_made || 0, other_earnings: +f.other_earnings || 0, remarks: f.remarks };
      if (record) { await api.put(`/finance/settlements/${record.id}`, body); toast.success("Settlement updated — contractor ledger & cash synced"); }
      else { await api.post("/finance/settlements", body); toast.success("Settlement saved — contractor ledger & cash updated"); }
      onSaved(); reset(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const reset = () => { setF({ contractor_id: "", period_from: "", period_to: "", previous_advance: "", payments_made: "", other_earnings: "", remarks: "" }); setPreview(null); };

  const bal = preview ? preview.total_payable + (+f.other_earnings || 0) - (+f.previous_advance || 0) - (+f.payments_made || 0) : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (reset(), onClose())}>
      <DialogContent data-testid="settlement-dialog">
        <DialogHeader><DialogTitle className="font-display">{record ? "Edit Labour Settlement" : "Labour Settlement"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contractor" required className="col-span-2"><SelectInput options={contractors.map((c) => ({ value: c.id, label: `${c.name} (${c.code})` }))} value={f.contractor_id} onChange={(e) => { setF({ ...f, contractor_id: e.target.value }); setPreview(null); }} data-testid="settlement-contractor" /></Field>
          <Field label="Period From" required><TextInput type="date" value={f.period_from} onChange={(e) => { setF({ ...f, period_from: e.target.value }); setPreview(null); }} data-testid="settlement-from" /></Field>
          <Field label="Period To" required><TextInput type="date" value={f.period_to} onChange={(e) => { setF({ ...f, period_to: e.target.value }); setPreview(null); }} data-testid="settlement-to" /></Field>
        </div>
        <button onClick={calc} disabled={busy} data-testid="calc-settlement-btn" className="flex h-10 items-center justify-center gap-2 rounded-lg border border-emerald-700 text-emerald-800 font-semibold hover:bg-emerald-50">
          <Calculator size={16} /> Calculate Earnings
        </button>
        {preview && (
          <>
            <div className="rounded-lg bg-emerald-50 p-4 text-sm space-y-1" data-testid="settlement-preview">
              <div className="flex justify-between"><span className="text-stone-600">Work Payment ({preview.entries} entries)</span><span>{fmtMoney(preview.work_payment_total)}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">Dala (labour receives full)</span><span>{fmtMoney(preview.dala_total)}</span></div>
              <div className="flex justify-between border-t border-emerald-200 pt-1 font-semibold"><span>Total Payable</span><span className="text-emerald-800">{fmtMoney(preview.total_payable)}</span></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Other Earnings"><NumberInput value={f.other_earnings} onChange={(e) => setF({ ...f, other_earnings: e.target.value })} /></Field>
              <Field label="Previous Advance"><NumberInput value={f.previous_advance} onChange={(e) => setF({ ...f, previous_advance: e.target.value })} /></Field>
              <Field label="Paid Now"><NumberInput value={f.payments_made} onChange={(e) => setF({ ...f, payments_made: e.target.value })} data-testid="settlement-paid" /></Field>
            </div>
            <div className="flex justify-between rounded-lg bg-amber-50 p-3 text-sm font-bold"><span>Balance Payable</span><span className="text-amber-800">{fmtMoney(bal)}</span></div>
            <button onClick={save} disabled={busy} data-testid="save-settlement-btn" className="flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <HandCoins size={18} />} {record ? "Update Settlement" : "Save Settlement"}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Spin = () => <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-emerald-700" size={28} /></div>;
